<!--
    Standortkarte der Section Karte (Figma 695:4591).

    Technisch wie in aesthetify-website:
      - maplibre-gl (~280 KB, ~1,6 s CPU-Init auf Mobile) NICHT statisch laden.
        Der dynamische Import landet in einem eigenen Chunk und blockiert beim
        Vue-Mount nicht den Main-Thread. Seiten ohne Karte zahlen gar nichts.
      - Initialisierung erst, wenn die Karte in Sichtweite kommt
        (IntersectionObserver mit Vorlauf).
      - OpenFreeMap als Tile-Quelle: kein API-Key, kein Nutzungslimit.

    Dazu ein zweites Gate: die Zustimmung zur Cookiebot-Kategorie „Präferenzen“
    (composables/useConsent.js). maplibre selbst setzt nichts auf dem Endgerät ab
    — kein Cookie, kein Storage —, aber die Kacheln kommen von
    tiles.openfreemap.org, und damit geht die IP-Adresse an einen Dritten.
    Cookiebots `data-blockingmode="auto"` greift dafür nicht: es blockt
    <script src>-Tags im Markup, nicht ein fetch aus dem eigenen Bundle heraus.
    Ohne Zustimmung steht statt der Karte das Ersatzbild mit einem Hinweis; sein
    Knopf schaltet genau diese eine Kategorie frei und lässt die übrigen stehen.
    Ein Widerruf beendet eine schon laufende Karte (destroyMap).

    Die Karte ist bedienbar: ziehen, zoomen, Pfeiltasten. Damit sie die Seite
    nicht kapert, laufen die Gesten "kooperativ" — Zoomen am Rad erst mit
    ctrl/⌘, auf dem Touchscreen erst mit zwei Fingern. Ohne das scrollt die
    Seite auf halber Höhe nicht weiter, sondern zoomt.

    Mit `static` ist es dieselbe echte Karte, nur ohne Bedienung: kein Cluster,
    keine Filter, keine Zoom-Schalter (Section "Karte (statisch)",
    sectionStaticMap.twig). `marker` setzt dort eine Nadel auf die gepflegte
    Adresse, und die Adresse steht als Fähnchen von Anfang an offen daneben —
    ohne Bedienung gibt es nichts, was sie später aufklappen könnte. Das
    Ersatzbild kommt als Slot-Inhalt und liegt unter der Leinwand — es trägt die
    Höhe der Fläche und bleibt sichtbar, wenn maplibre nicht startet.

    Einfärbung: Figma legt eine Fläche in Markenfarbe mit `mix-blend-mode: hue`
    über einen Kartenausschnitt. Als CSS-Layer über der Karte trägt das nicht —
    Hue behält die Sättigung der Basemap, und deren gelbe Autobahnen und blaues
    Wasser werden damit zu neonfarbenen Bändern. Stattdessen die blasse
    "positron"-Basemap (grau, weisse Strassen — schon fast der Entwurf) und
    Land, Wasser und Grün in Aufhellungen der Markenfarbe.

    Die Marke kommt aus den CSS-Tokens, nicht aus dem Template: `--color-brand`
    und `--color-brand-tint` schaltet `.brand-gvo` am <html> um (siehe
    config/_brand.scss), und WebGL kann keine Custom Properties lesen — deshalb
    werden sie hier am Container ausgelesen. Die Marker brauchen das nicht, die
    stehen direkt auf dem Token. Ein Wechsel der Marken-Klasse zur Laufzeit färbt
    die Karte nach (MutationObserver), damit ein Umschalter nicht daran scheitert.

    Der Wurzelknoten ist ein echtes Rasterkind der Section (kein
    `display: contents` — sonst greift die Stapelregel `.section > *` nicht und
    die Karte verschwindet hinter dem Sectionhintergrund). Er stapelt Filterzeile
    über Kartenfläche und gibt die Spalten der Section als Subgrid weiter: die
    Filterzeile sitzt auf den Spalten, die der Text freilässt, die Kartenfläche
    auf den Medienspalten. Die Masse dazu stehen in sections/_section-map.scss.

    Wo die Karte im Raster sitzt, sagt die Section: in der Section "Karte" teilt
    sie sich das Layout mit "Text + Media" (Block `text-media`), und der
    Wurzelknoten bekommt von dort `text-media__media`. `text-media__surface` an
    der Kartenfläche ist das Gegenstück dazu — die Fläche, die Verhältnis und
    diagonalen Anschnitt trägt. Die Klasse steht hier fest und wirkt nur unter
    `.text-media`; die statische Karte bringt den Block nicht mit und bleibt
    davon unberührt.
-->
<script setup>
import 'maplibre-gl/dist/maplibre-gl.css'
// Der Worker, in dem maplibre die Vektor-Tiles zerlegt — ohne ihn bleibt die
// Karte leer. maplibre sucht ihn zur Laufzeit neben seinem eigenen Modul
// (`new URL('./maplibre-gl-worker.mjs', import.meta.url)`); diesen berechneten
// Pfad sieht Rollup nicht, im Build fehlte die Datei und der Worker lief in
// einen 404. Nur im Dev-Server ging es gut, weil dort die unveränderten
// Paketdateien liegen. `?worker&url` baut ihn als eigenes Bundle; die Konstante
// hier ist nur dessen Adresse, geladen wird sie erst beim Start des Workers.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { useConsent } from '@/composables/useConsent'
// Der Pfeil des CTA. In Twig setzt ihn `svg()` ein (_/partials/_cta-link.twig);
// hier kommt er über vite-svg-loader als Komponente herein. Beide Dateien sind
// gleich aufgebaut, die Platte hängt an `path[opacity]`.
import ButtonArrow from '@/assets/button-arrow.svg'

// Markenfarbe auf Weiss aufgehellt. maplibre versteht kein color-mix(), die
// Rechnung muss also hier passieren.
function tint(hex, share) {
    const raw = String(hex).replace('#', '')
    // Kein sechsstelliger Hex (etwa ein Farbname): unvermischt durchgeben, sonst
    // stünde rgb(NaN, …) im Style und maplibre wirft die ganze Ebene weg.
    if (!/^[0-9a-f]{6}$/i.test(raw)) return hex

    const channels = raw.match(/../g).map((part) => Number.parseInt(part, 16))
    const [r, g, b] = channels.map((value) => Math.round(value * share + 255 * (1 - share)))

    return `rgb(${r}, ${g}, ${b})`
}

// Markenfarben am Element auslesen. Custom Properties sind zum Zeitpunkt des
// Lesens schon aufgelöst (`--color-brand: var(--color-arctic-100)` kommt als
// Hexwert zurück), die Marke der Seite steckt also hier drin.
function brandColors(element) {
    const styles = getComputedStyle(element)
    const read = (token, fallback) => styles.getPropertyValue(token).trim() || fallback

    return {
        brand: read('--color-brand', '#53346F'),
        tint: read('--color-brand-tint', '#F6F6F7'),
    }
}

// Die Basemap "positron" ist grau und blass — Gebäude, Strassen und Ortsnamen
// passen so schon. Farbe bekommen die Flächen: Land in der Markentönung, Wasser
// und Grün in stärkeren Aufhellungen der Markenfarbe.
function styleTweaks({ brand, tint: land }) {
    const water = tint(brand, 0.22)
    const green = tint(brand, 0.1)

    return [
        ['background', 'background-color', land],
        ['landuse_residential', 'fill-color', tint(brand, 0.08)],
        ['water', 'fill-color', water],
        ['waterway', 'line-color', water],
        ['park', 'fill-color', green],
        ['landcover_wood', 'fill-color', green],
        ['label_city', 'text-color', '#6E7382'],
        ['label_town', 'text-color', '#6E7382'],
        ['label_village', 'text-color', '#6E7382'],
        ['label_other', 'text-color', '#8A8FA0'],
        ['water_name_point_label', 'text-color', '#8A8FA0'],
        ['water_name_line_label', 'text-color', '#8A8FA0'],
    ]
}

const {
    locations = '[]',
    filters = '[]',
    allLabel = 'Alle',
    zoom = 12,
    lat = 51.4696,
    lng = 6.8514,
    static: isStatic = false,
    marker: showMarker = false,
    address = '',
    markerText = '',
    consentText = 'Die Karte kommt von OpenFreeMap — beim Laden geht die IP-Adresse dorthin. '
        + 'Dafür fehlt bisher die Zustimmung.',
    consentCta = 'Karte laden',
} = defineProps({
    // JSON aus Twig — Vue-Props über Attribute können keine Objekte tragen.
    locations: String,
    filters: String,
    allLabel: String,
    // Zahlen aus einem Twig-Attribut kommen als Zeichenkette an: Vue wandelt bei
    // In-DOM-Templates nichts um (nur Boolean-Props bekommen eine
    // Sonderbehandlung). Deshalb beide Typen erlauben und unten einmal umrechnen
    // — als "15" landete der Zoom sonst wörtlich in maplibre.
    zoom: [Number, String],
    lat: [Number, String],
    lng: [Number, String],
    // Section "Karte (statisch)": ein Punkt, keine Bedienung. Als Boolean-Prop
    // deklariert, damit das blosse Attribut `static` (ohne Wert) als true
    // ankommt — als String wäre es "" und damit falsch herum.
    static: Boolean,
    // Statische Karte mit Punkt auf der Adresse (Schalter "Marker anzeigen").
    // Auch hier ein Boolean-Prop, damit das blosse Attribut `marker` als true
    // ankommt — und sein Fehlen als false. Deshalb entscheidet das Template, ob
    // das Attribut überhaupt geschrieben wird; ein `marker="false"` käme als
    // nicht-leere Zeichenkette an und wäre wahr.
    marker: Boolean,
    address: String,
    // Hinweis und Knopfbeschriftung, solange die Cookie-Zustimmung fehlt. Beide
    // stehen im Settings-Single (Reiter „Karte“) und kommen über
    // _/partials/_map-consent-attrs.twig herein. Ungepflegt gelten die Vorgaben.
    consentText: String,
    consentCta: String,
    // Gepflegter Text fürs Fähnchen (CKEditor, siehe sectionStaticMap.twig).
    // Gefüllt verdrängt er die Adresse — die kommt von Maps als eine Zeile und
    // kann keine Umbrüche.
    markerText: String,
})

const zoomLevel = computed(() => Number(zoom))
const center = computed(() => ({ lng: Number(lng), lat: Number(lat) }))

const parsed = computed(() => {
    try {
        return JSON.parse(locations)
            .filter((location) => location.lat && location.lng)
            .map((location) => ({
                ...location,
                // Mehrere Arten je Standort: ein Objekt kann Wohnen UND Gewerbe
                // sein. Deshalb eine Liste, nicht ein Wert.
                kinds: Array.isArray(location.kinds) ? location.kinds : [],
            }))
    } catch {
        return []
    }
})

// Die Arten für die Filterzeile: `{ handle, title }` je Option des Feldes
// "Nutzung", zusammengefasst (Grundstücke unter Gewerbe) und nur die, die auf
// der Karte vorkommen — beides entscheidet das Template.
const kinds = computed(() => {
    try {
        return JSON.parse(filters)
    } catch {
        return []
    }
})

const numberFormat = new Intl.NumberFormat('de-DE')

function featureCollection(list) {
    return {
        type: 'FeatureCollection',
        features: list.map((location, index) => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [Number(location.lng), Number(location.lat)],
            },
            properties: {
                // Eigener Schlüssel für die Entdopplung: die Geometrie taugt nicht
                // dafür, sie kommt aus dem Tile und ist je Zoomstufe anders
                // gerundet (6.839400000 vs 6.839400021).
                key: String(index),
                title: location.title,
                address: location.address ?? '',
                // Gepflegte Angaben des Standorts (siehe FACTS). Null heisst
                // "nicht gepflegt" — die Zeile im Fähnchen entfällt dann. 0 ist
                // ein gepflegter Wert und würde gezeigt, deshalb kein `||`.
                area: location.area ?? null,
                areaLabel: location.areaLabel ?? null,
                rooms: location.rooms ?? null,
                units: location.units ?? null,
                commercialUnits: location.commercialUnits ?? null,
                status: location.status ?? null,
                completion: location.completion ?? null,
                // Flach und nicht als Objekt: die Properties gehen durch die
                // Vektor-Tiles der Quelle, und die tragen nur Skalare — ein
                // verschachteltes `image` käme als JSON-Text zurück.
                imageSrc: location.image?.src ?? null,
                imageSrc2x: location.image?.src2x ?? null,
                imageAlt: location.image?.alt ?? '',
            },
        })),
    }
}

// Einfachauswahl: eine Pille zeigt genau ihre Art, `null` heisst "alle". Kein
// Abschalten per zweitem Klick: zurück geht es über "Alle".
const activeKind = ref(null)

// Ein Standort zeigt sich unter JEDER seiner Arten — `includes`, nicht `===`:
// die acht gemischten Objekte gehören unter Wohnen wie unter Gewerbe.
const visible = computed(() =>
    activeKind.value === null
        ? parsed.value
        : parsed.value.filter((location) => location.kinds.includes(activeKind.value)),
)

// Cluster: nahe Standorte fassen sich zu einem Kreis zusammen, die Zahl darin ist
// ihre Anzahl. Ein Klick (oder Hineinzoomen) teilt den Kreis auf, bis am Ende
// jeder Standort als einzelner Marker steht.
const SOURCE_ID = 'locations'
const QUERY_LAYER_ID = 'locations-query'

// Luft, die das Fähnchen zum Rand der Kartenfläche lässt.
const POPUP_EDGE_GAP = 16

// maplibre deckelt die Breite des Fähnchens per Inline-Style — aus dem SCSS ist
// das nicht erreichbar. Als var() übergeben bleibt das Mass trotzdem dort, wo
// die anderen Masse der Karte stehen (sections/_section-map.scss). Ohne eigene
// Angabe wären es maplibres 240px, und die Faktenliste schnitte rechts ab.
const POPUP_MAX_WIDTH = 'var(--map-box-popup-max-width)'

// Luft zwischen Nadel und Fähnchen (siehe pinPopupOffset).
const POPUP_PIN_GAP = 8

const rootRef = useTemplateRef('rootRef')
const map = ref()
let maplibre = null
let markers = []
let io = null
let ro = null
let mo = null
let releaseMapWheel = null
let popup = null
let userMoved = false
let renderedIds = ''
// Schlüssel des Standorts, dessen Fähnchen offen ist (siehe pointMarker). Der
// Marker-Neubau nimmt Fähnchen mit — daran erkennt er, welches wieder aufgeht.
let openPopupKey = null

// Die Kacheln kommen von tiles.openfreemap.org — beim Laden geht die IP-Adresse
// an einen Dritten. Also erst mit Zustimmung, Kategorie „Präferenzen“.
const { granted: consent, grant: grantConsent } = useConsent()

// Der CTA trägt den Pfeil hinter dem letzten Wort, nicht neben dem Textblock:
// letztes Wort und Icon stecken zusammen in einer umbruchfesten Gruppe. Dieselbe
// Aufteilung wie in _/partials/_cta-link.twig.
const consentCtaParts = computed(() => {
    const words = String(consentCta).trim().split(' ')

    const lead = words.slice(0, -1).join(' ')

    return {
        // Das trennende Leerzeichen gehört in die Zeichenkette: zwischen zwei
        // Elementen fasst Vue Whitespace zusammen und verschluckt ihn am Rand.
        lead: lead ? `${lead} ` : '',
        last: words.at(-1) ?? '',
    }
})
// Zweites Signal neben der Zustimmung: erst wenn die Karte in Sichtweite kommt.
const inView = ref(false)
let starting = false

defineExpose({ map })

function pinElement({ count, large, label }) {
    // Echter Button: die Kreise teilen beim Klick ihr Cluster auf, sind also
    // Bedienelement — und müssen mit der Tastatur erreichbar sein.
    const el = document.createElement('button')
    el.type = 'button'
    el.className = 'map-box__pin'
    el.setAttribute('aria-label', label)

    // Ohne Zahl steht dort ein einzelner Standort: die Nadel aus dem Entwurf
    // (Figma 1234:5375), kein Kreis — die Zahl gehört dem Cluster.
    if (!count) {
        el.classList.add('map-box__pin--marker')

        return el
    }

    el.classList.add('map-box__pin--bubble')

    if (large) el.classList.add('map-box__pin--large')

    const countEl = document.createElement('strong')
    countEl.className = 'map-box__pin-count'
    countEl.textContent = numberFormat.format(count)
    el.append(countEl)

    return el
}

// Versatz für ein Fähnchen an der Nadel: über dem Punkt muss es die Nadel
// überspringen, darunter reicht die Luft der Spitze. Die Höhe steht als clamp()
// im CSS — also gemessen, und erst am eingehängten Element. Die Tabelle je Anker
// ist die Bauform, die maplibre für seinen eigenen Standardmarker nutzt; eine
// blosse Zahl wäre symmetrisch und schöbe das Fähnchen unten genauso weit weg.
function pinPopupOffset(element) {
    const above = -element.offsetHeight - POPUP_PIN_GAP
    const corner = Math.round(POPUP_PIN_GAP / Math.SQRT2)

    return {
        center: [0, 0],
        top: [0, POPUP_PIN_GAP],
        'top-left': [corner, POPUP_PIN_GAP],
        'top-right': [-corner, POPUP_PIN_GAP],
        bottom: [0, above],
        'bottom-left': [corner, above],
        'bottom-right': [-corner, above],
        left: [POPUP_PIN_GAP, above / 2],
        right: [-POPUP_PIN_GAP, above / 2],
    }
}

function clusterMarker(feature, large) {
    const { cluster_id: clusterId, point_count: count } = feature.properties

    const el = pinElement({
        count,
        large,
        label: `${count} Standorte — hineinzoomen`,
    })

    el.addEventListener('click', () => {
        const source = map.value?.getSource(SOURCE_ID)
        if (!source) return

        // Aufteilen ist eine Nutzerabsicht: danach nicht mehr automatisch auf den
        // Gesamtausschnitt zurückspringen.
        userMoved = true

        source.getClusterExpansionZoom(clusterId).then((nextZoom) => {
            map.value?.easeTo({
                center: feature.geometry.coordinates,
                zoom: nextZoom,
                duration: 500,
            })
        })
    })

    return new maplibre.Marker({ element: el }).setLngLat(feature.geometry.coordinates)
}

// Statische Karte: eine Nadel, kein Cluster. Deshalb kein Button — hier gibt es
// nichts zu bedienen, und ein Tabstopp ohne Wirkung wäre nur ein Hindernis. Die
// Der Text steht im Fähnchen daneben (siehe addressPopup).
function staticMarker() {
    const el = document.createElement('div')
    el.className = 'map-box__pin map-box__pin--marker'
    el.setAttribute('aria-hidden', 'true')

    // `anchor: bottom` setzt die Spitze auf die Koordinate — ohne das stünde die
    // Nadel mit ihrer Mitte darauf und zeigte daneben.
    return new maplibre.Marker({ element: el, anchor: 'bottom' }).setLngLat(center.value)
}

// Das Fähnchen der statischen Karte hängt NICHT am Marker: `Marker.setPopup()`
// verpasst dem Markerelement `tabindex="0"` und eine Button-Rolle, damit man das
// Fähnchen auf- und zuklappen kann. Hier gibt es nichts zu klappen — und ein
// fokussierbares Element mit `aria-hidden` wäre ein Fehler, keine Bedienhilfe.
// Also ein eigenes Popup auf derselben Koordinate: Text auf der Karte, sonst
// nichts.
function addressPopup(pin) {
    return new maplibre.Popup({
        // Wie am Standortmarker: über der Nadel, nicht auf ihr. Die Nadel hängt
        // hier schon in der Karte, ihre Höhe ist also messbar.
        offset: pinPopupOffset(pin),
        // Kein Schliessen-Kreuz und kein Zuklappen per Kartenklick — das
        // Fähnchen gehört zum Bild, und ohne Bedienung wäre es nicht wieder
        // aufzubekommen.
        closeButton: false,
        closeOnClick: false,
        // Sonst zieht maplibre den Fokus beim Öffnen ins Fähnchen. Geöffnet wird
        // hier beim Laden der Karte, nicht auf Klick — der Sprung würde die
        // Seite unter dem Nutzer wegscrollen.
        focusAfterOpen: false,
        maxWidth: POPUP_MAX_WIDTH,
    })
        .setLngLat(center.value)
        .setDOMContent(addressContent())
}

// Gepflegter Text hat Vorrang. Als einziger Inhalt hier kommt er als Markup
// herein: CKEditor mit `purifyHtml`, also serverseitig gesäubert — und mit
// textContent gingen Absätze und Umbrüche verloren, wegen derer es das Feld gibt.
function markerTextContent() {
    const content = document.createElement('div')
    content.className = 'map-box__popup-text'
    content.innerHTML = markerText

    return content
}

// Adresse in zwei Zeilen, wie im Fähnchen der Standortkarte: Strasse fett, der
// Rest (PLZ, Ort, Land) darunter. Als DOM-Knoten und nicht per setHTML — der Text
// kommt aus der Pflege und soll Text bleiben.
function addressContent() {
    if (markerText) return markerTextContent()

    const parts = address.split(',').map((part) => part.trim()).filter(Boolean)

    const content = document.createElement('div')
    const streetEl = document.createElement('strong')
    // Ohne Komma in der Adresse bleibt es bei einer Zeile — dann steht die ganze
    // Adresse fett, statt dass die erste Zeile leer bliebe.
    streetEl.textContent = parts.shift() ?? address
    content.append(streetEl)

    if (parts.length) {
        content.append(document.createElement('br'), document.createTextNode(parts.join(', ')))
    }

    return content
}

// Die Angaben unter der Adresse, in dieser Reihenfolge. Die Beschriftung gehört
// dazu: "24" allein sagt nichts. `text: true` heisst "kommt schon fertig aus dem
// Template" — Status als Dropdown-Beschriftung, Fertigstellung als Datum, das
// Craft formatiert hat.
//
// `labelKey` gibt der Pflege das letzte Wort über die Beschriftung: die Fläche
// heisst je Entry-Type Wohn-, Gewerbe- oder Grundstücksfläche, und wie genau
// steht im Feldlayout (siehe sectionMap.twig). `label` ist dann nur noch der
// Rückfall, wenn nichts mitkommt.
const FACTS = [
    { key: 'area', label: 'Fläche', labelKey: 'areaLabel', unit: '\u00a0m²' },
    { key: 'rooms', label: 'Zimmer' },
    { key: 'units', label: 'Wohneinheiten' },
    { key: 'commercialUnits', label: 'Gewerbeeinheiten' },
    { key: 'status', label: 'Status', text: true },
    { key: 'completion', label: 'Fertigstellung', text: true },
]

// Zahlen gruppiert (1.250) und mit geschütztem Leerzeichen vor der Einheit, damit
// "1.250 m²" nicht umbricht. Nicht gepflegt heisst: keine Zeile, und die 0 zählt
// dazu — "Wohneinheiten 0" oder "Fläche 0 m²" sagt nichts über das Objekt aus.
function factRows(properties) {
    return FACTS.flatMap(({ key, label, labelKey, unit = '', text = false }) => {
        const raw = properties[key]

        // querySourceFeatures liefert nicht gepflegte Werte je nach Tile als null
        // oder gar nicht — beide Fälle sind hier dasselbe.
        if (raw === null || raw === undefined || raw === '') return []

        const heading = (labelKey && properties[labelKey]) || label

        if (text) return [{ label: heading, value: String(raw) }]

        const value = Number(raw)

        if (!Number.isFinite(value) || value === 0) return []

        return [{ label: heading, value: `${numberFormat.format(value)}${unit}` }]
    })
}

// Vorschaubild über dem Titel. Zwei Auflösungen aus der Pflege (1x/2x), die
// Fläche gibt die CSS-Breite vor — die Attribute width/height stehen nur für das
// Seitenverhältnis, damit nichts springt, solange das Bild lädt.
function imageElement({ imageSrc, imageSrc2x, imageAlt }) {
    if (!imageSrc) return null

    const image = document.createElement('img')
    image.className = 'map-box__popup-image'
    image.src = imageSrc

    if (imageSrc2x) image.srcset = `${imageSrc} 1x, ${imageSrc2x} 2x`

    image.width = 320
    image.height = 180
    // Kein Alternativtext gepflegt: leer lassen. Das Bild wiederholt nur den
    // Standort, der als Titel direkt darunter steht — ein Platzhaltertext wäre
    // für Screenreader Lärm.
    image.alt = imageAlt || ''
    image.loading = 'lazy'
    image.draggable = false

    return image
}

// Als Definitionsliste, und über DOM-Knoten statt setHTML: die Werte kommen aus
// der Pflege und sollen Text bleiben. Ohne eine einzige Angabe gibt es die Liste
// gar nicht — ein leeres <dl> würde nur den Abstand aufziehen.
function factsElement(properties) {
    const rows = factRows(properties)

    if (!rows.length) return null

    const list = document.createElement('dl')
    list.className = 'map-box__popup-facts'

    for (const { label, value } of rows) {
        const term = document.createElement('dt')
        term.textContent = label

        const description = document.createElement('dd')
        description.textContent = value

        list.append(term, description)
    }

    return list
}

// maplibre hängt seinen Rad-Handler an den LEINWAND-Container. Alles, was
// daneben oder darüber liegt, sieht er nie — und davon gibt es mehr, als es
// aussieht:
//
//   - Die Zoom-Schalter und die Attribution stehen in
//     `.maplibregl-control-container`, einem Geschwisterknoten der Leinwand.
//   - Auf der Section "Karte" liegt die TEXTSPALTE über der Kartenfläche (der
//     Entwurf schneidet den Text in die Karte hinein). Dort ist das Ziel des
//     Rad-Ereignisses der Absatz, nicht die Karte — obwohl der Zeiger sichtbar
//     auf der Karte steht. Gemessen: rund ein Zehntel der Kartenfläche.
//
// Deshalb entscheidet nicht der Zielknoten, sondern die GEOMETRIE: liegt der
// Zeiger im Rechteck der Kartenfläche, gilt die Karte. Genau das ist auch die
// Anforderung ("Zeiger auf der Karte"), und sie bleibt richtig, wenn morgen noch
// etwas über der Karte liegt.
//
// Der Listener hängt an der Section und erfasst, nicht am Fenster: so liegt er
// vor maplibres Handler und deckt die Überlagerungen ab, ohne jeder Seite mit
// Karte einen nicht-passiven Rad-Listener am Fenster aufzuhalsen.
//
// Ohne Zusatztaste wird nichts abgefangen: dann soll die Seite scrollen, das ist
// der Sinn der kooperativen Gesten.
function guardMapWheel(instance) {
    const container = instance.getContainer()
    const canvas = instance.getCanvasContainer()
    // Die Kartenfläche ist das Elternteil des maplibre-Containers; die Section
    // darüber ist der Knoten, unter dem auch die Textspalte hängt.
    const surface = container.parentElement
    const scope = container.closest('.section') ?? surface

    if (!canvas || !surface || !scope) return null

    const onWheel = (event) => {
        // Dieselbe Regel wie maplibre intern: auf dem Mac ⌘, sonst ctrl. ctrl gilt
        // zusätzlich überall, weil der Trackpad-Pinch als ctrl + Rad ankommt.
        const bypassKey = navigator.userAgent.includes('Mac') ? event.metaKey : event.ctrlKey

        if (!event.ctrlKey && !bypassKey) return

        const box = surface.getBoundingClientRect()
        const overMap =
            event.clientX >= box.left &&
            event.clientX <= box.right &&
            event.clientY >= box.top &&
            event.clientY <= box.bottom

        if (!overMap) return

        event.preventDefault()

        // Über der Leinwand zoomt maplibre selbst: das Ereignis läuft nach dieser
        // Erfassung ganz normal weiter zu seinem Handler.
        if (canvas.contains(event.target)) return

        // Sonst neu aufgebaut hinschicken — ein Ereignis lässt sich nicht zweimal
        // ausliefern, und maplibre liest ohnehin nur diese Felder.
        canvas.dispatchEvent(
            new WheelEvent('wheel', {
                bubbles: true,
                cancelable: true,
                clientX: event.clientX,
                clientY: event.clientY,
                deltaX: event.deltaX,
                deltaY: event.deltaY,
                deltaZ: event.deltaZ,
                deltaMode: event.deltaMode,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
                shiftKey: event.shiftKey,
                altKey: event.altKey,
            }),
        )
    }

    // passive: false, sonst darf preventDefault() nichts bewirken.
    const options = { passive: false, capture: true }
    scope.addEventListener('wheel', onWheel, options)

    // Abmelden nur mit derselben capture-Angabe, sonst bleibt der Listener hängen.
    return () => scope.removeEventListener('wheel', onWheel, options)
}

function pointMarker(feature) {
    const { title, address } = feature.properties

    // Einzelner Standort: die Nadel, kein Kreis mit Zahl — die Zahl gehört dem
    // Cluster. `anchor: bottom` setzt ihre Spitze auf die Koordinate.
    const pin = pinElement({ count: 0, large: false, label: title || 'Standort' })
    const marker = new maplibre.Marker({ element: pin, anchor: 'bottom' })
        .setLngLat(feature.geometry.coordinates)

    if (title) {
        // setDOMContent statt setHTML: Titel und Adresse kommen aus der Pflege und
        // gehen so als Text durch, nicht als Markup.
        const content = document.createElement('div')

        const image = imageElement(feature.properties)

        if (image) content.append(image)

        const titleEl = document.createElement('strong')
        titleEl.textContent = title
        content.append(titleEl)

        // Der Titel ist meist schon die Strasse — dann steht in der zweiten Zeile
        // nur der Rest der Adresse (PLZ und Ort), nicht alles doppelt.
        const rest = address.startsWith(title)
            ? address.slice(title.length).replace(/^[\s,]+/, '')
            : address

        if (rest) {
            content.append(document.createElement('br'), document.createTextNode(rest))
        }

        const facts = factsElement(feature.properties)

        if (facts) content.append(facts)

        // maplibre hängt das Fähnchen IN den Kartencontainer, und der schneidet
        // ab. Mit Bild und allen fünf Angaben wird es höher als die Kartenfläche
        // (die ihre Höhe aus dem Seitenverhältnis bezieht, auf 1440 sind das
        // rund 318 px) — dann fehlte die untere Hälfte. Deshalb beim Öffnen ein
        // Deckel auf die Inhaltshöhe.
        //
        // Erst beim Öffnen, weil vorher weder die Kartenhöhe noch die Höhe von
        // Rahmen und Spitze feststehen. Beide werden gemessen statt geschätzt:
        // sonst müssten Innenabstand und Versatz hier als Zahlen doppelt stehen.
        content.className = 'map-box__popup-body'

        const popupInstance = new maplibre.Popup({
            // Der Versatz zur Nadel kommt erst beim Öffnen: ihre Höhe steht als
            // clamp() im CSS und ist am noch nicht eingehängten Element nicht
            // messbar. Die Angabe hier hält maplibre nur davon ab, selbst einen
            // Standardversatz zu setzen (Marker.setPopup).
            offset: 0,
            closeButton: false,
            maxWidth: POPUP_MAX_WIDTH,
        }).setDOMContent(content)

        popupInstance.on('open', () => {
            openPopupKey = String(feature.properties.key)

            // Jetzt hängt die Nadel in der Karte, ihre Höhe ist messbar. `setOffset`
            // rechnet die Lage gleich neu — noch vor dem ersten Bild, es läuft alles
            // in derselben Aufgabe.
            popupInstance.setOffset(pinPopupOffset(pin))

            const container = map.value?.getContainer()
            const element = popupInstance.getElement()

            if (!container || !element) return

            // Deckel auf die Inhaltshöhe: maplibre hängt das Fähnchen IN den
            // Kartencontainer, und der schneidet ab. Mit Bild und allen fünf
            // Angaben wird es höher als die Kartenfläche (die ihre Höhe aus dem
            // Seitenverhältnis bezieht — auf 1440 sind das rund 318 px), dann
            // fehlte die untere Hälfte. Rahmen und Spitze werden gemessen statt
            // geschätzt, sonst stünden Innenabstand und Versatz hier als Zahlen
            // ein zweites Mal.
            const chrome = element.offsetHeight - content.offsetHeight

            content.style.maxHeight = `${Math.max(0, container.clientHeight - chrome - 2 * POPUP_EDGE_GAP)}px`

            // Den Anker hat maplibre mit der ungedeckelten Höhe gewählt. Denselben
            // Punkt noch einmal setzen lässt es neu rechnen — jetzt mit der Höhe,
            // die das Fähnchen wirklich hat.
            popupInstance.setLngLat(popupInstance.getLngLat())

            // Der Anker allein reicht nicht: maplibre entscheidet "über oder unter
            // dem Punkt" an einer Schwelle, die nur trägt, solange das Fähnchen
            // deutlich kleiner als die Karte ist. Ist es fast so hoch wie sie —
            // mit Bild und allen Angaben der Normalfall —, wählt es "darunter",
            // auch wenn dort kein Platz ist. Also den Überstand messen und die
            // Karte darum schieben; das Fähnchen hängt am geografischen Punkt und
            // wandert mit.
            //
            // Das Lesen der Kästen erzwingt das Layout mit Deckel und neuem Anker
            // — deshalb kein Warten auf den nächsten Frame.
            const box = element.getBoundingClientRect()
            const area = container.getBoundingClientRect()

            // Überstand unten und rechts schiebt die Karte nach, Überstand oben
            // und links zurück. Passt es in einer Achse nach keiner Seite, hat die
            // obere linke Kante Vorrang — dort steht der Titel.
            const panX = Math.max(0, box.right - area.right + POPUP_EDGE_GAP)
                - Math.max(0, area.left - box.left + POPUP_EDGE_GAP)
            const panY = Math.max(0, box.bottom - area.bottom + POPUP_EDGE_GAP)
                - Math.max(0, area.top - box.top + POPUP_EDGE_GAP)

            if (!panX && !panY) return

            map.value.panBy([panX, panY], { duration: 250 })
        })

        // Nur das vom Nutzer geschlossene Fähnchen vergessen. Beim Neubau schliesst
        // maplibre es auch — dort hat refreshMarkers den Schlüssel aber schon
        // gesichert, um es wieder aufzumachen.
        popupInstance.on('close', () => {
            if (openPopupKey === String(feature.properties.key)) openPopupKey = null
        })

        marker.setPopup(popupInstance)
    }

    return marker
}

// Nadeln und Kreise sind DOM-Marker und keine Kartenebene: nur so tragen sie
// Projektschrift, Markenfarbe, Fokusring und ein Label. Geclustert wird trotzdem
// von maplibre (supercluster in der Quelle), abgefragt wird das Ergebnis hier.
function refreshMarkers() {
    if (!map.value || !maplibre || !map.value.getSource(SOURCE_ID)) return
    // querySourceFeatures liefert je Tile, an Tile-Grenzen also Doppelte.
    const seen = new Set()
    const features = []

    for (const feature of map.value.querySourceFeatures(SOURCE_ID)) {
        const { cluster, cluster_id: clusterId, key } = feature.properties
        const id = cluster ? `cluster-${clusterId}` : `point-${key}`

        if (seen.has(id)) continue

        seen.add(id)
        features.push(feature)
    }

    // Gleiche Menge wie zuletzt: nicht neu bauen. `idle` feuert auch ohne
    // Änderung, und jeder Neuaufbau nimmt der Karte kurz alle Marker.
    const ids = [...seen].sort().join('|')
    if (ids === renderedIds) return

    renderedIds = ids

    // Zwei Grössen wie im Entwurf (64 und 50 auf 2560): gross ab der Hälfte des
    // grössten Clusters, das gerade auf der Karte steht.
    const counts = features.map((feature) => feature.properties.point_count ?? 0)
    const largeFrom = Math.max(...counts, 0) / 2

    // Vor dem Abräumen merken: `marker.remove()` nimmt das Fähnchen mit und meldet
    // es als geschlossen, danach wüsste hier niemand mehr davon.
    const reopenKey = openPopupKey

    markers.forEach((marker) => marker.remove())
    markers = features.map((feature) => {
        const marker = feature.properties.cluster
            ? clusterMarker(feature, feature.properties.point_count >= largeFrom)
            : pointMarker(feature)

        return marker.addTo(map.value)
    })

    // Das offene Fähnchen auf dem neu gebauten Marker desselben Standorts wieder
    // aufmachen. Ohne das verschwindet es beim ersten `idle` nach dem Klick —
    // und der Einpass-Schub löst genau so eines aus.
    if (reopenKey === null) return

    const reopenIndex = features.findIndex(
        (feature) => !feature.properties.cluster && String(feature.properties.key) === reopenKey,
    )

    // Der Standort steckt jetzt in einem Cluster oder liegt ausserhalb: dann gibt
    // es nichts wieder aufzumachen.
    if (reopenIndex === -1) {
        openPopupKey = null
        return
    }

    markers[reopenIndex].togglePopup()
}

function addLocationSource(instance) {
    instance.addSource(SOURCE_ID, {
        type: 'geojson',
        data: featureCollection(visible.value),
        cluster: true,
        // Etwas mehr als eine Blase breit: sonst überdecken sich zwei Blasen,
        // statt sich zusammenzufassen.
        clusterRadius: 56,
        clusterMaxZoom: 15,
    })

    // Unsichtbare Ebene auf der Quelle: ohne eine Ebene lädt maplibre deren Tiles
    // nicht, und querySourceFeatures() käme leer zurück.
    instance.addLayer({
        id: QUERY_LAYER_ID,
        type: 'circle',
        source: SOURCE_ID,
        paint: { 'circle-radius': 0, 'circle-opacity': 0 },
    })

    instance.on('data', (event) => {
        if (event.sourceId === SOURCE_ID && event.isSourceLoaded) refreshMarkers()
    })

    // `idle` statt `moveend`: querySourceFeatures liefert die renderbaren Tiles,
    // und solange die neue Zoomstufe noch lädt, gehört die behaltene Elternstufe
    // dazu — man bekäme Cluster aus zwei Stufen gleichzeitig (das Cluster UND
    // seine Einzelstandorte). Bei `idle` ist genau eine Stufe im Bild.
    // Während der Bewegung wandern die vorhandenen Marker von allein mit.
    instance.on('idle', refreshMarkers)
}

// Filtern heisst: andere Daten in die Quelle geben. Ein Ebenen-Filter hilft nicht,
// weil die Cluster in der Quelle entstehen — sie müssen neu gerechnet werden.
function applyFilter() {
    const source = map.value?.getSource(SOURCE_ID)
    if (!source) return

    renderedIds = ''
    source.setData(featureCollection(visible.value))
}

function selectKind(handle) {
    // Nochmal dieselbe Pille ändert nichts — und soll die Karte nicht neu
    // aufbauen: setData() würfe jedes offene Fähnchen weg.
    if (activeKind.value === handle) return

    activeKind.value = handle
    applyFilter()
}

function showAll() {
    if (activeKind.value === null) return

    activeKind.value = null
    applyFilter()
}

// Ausschnitt immer über ALLE Standorte, nicht über die gefilterten: sonst
// springt die Karte bei jedem Filterklick, und die Karte ist hier ein Bild,
// kein Werkzeug.
function fitToLocations() {
    // Die statische Karte steht auf ihrem gepflegten Mittelpunkt und ihrer
    // gepflegten Zoomstufe — nichts einzupassen.
    if (isStatic) return
    if (!map.value || !maplibre || !rootRef.value || userMoved) return

    const points = parsed.value
    if (!points.length) return

    if (points.length === 1) {
        map.value.jumpTo({
            center: { lng: Number(points[0].lng), lat: Number(points[0].lat) },
            zoom: zoomLevel.value,
        })
        return
    }

    const bounds = points.reduce(
        (box, location) => box.extend([Number(location.lng), Number(location.lat)]),
        new maplibre.LngLatBounds(),
    )

    const { width, height } = rootRef.value.getBoundingClientRect()
    if (!width || !height) return

    map.value.fitBounds(bounds, {
        // Oben links liegt die Kerbe, in der Text und CTA stehen — dort keine
        // Punkte. Rechts und unten nur so viel Luft, dass die Blasen ganz im
        // Bild bleiben.
        padding: {
            top: Math.round(height * 0.22),
            left: Math.round(width * 0.1),
            right: Math.round(width * 0.06),
            bottom: Math.round(height * 0.1),
        },
        maxZoom: 14,
        duration: 0,
    })
}

function applyBrandColors() {
    if (!map.value || !rootRef.value) return

    for (const [id, prop, value] of styleTweaks(brandColors(rootRef.value))) {
        if (map.value.getLayer(id)) map.value.setPaintProperty(id, prop, value)
    }
}

function closePopups() {
    // Auf der statischen Karte gehört das Fähnchen zum Bild — einmal zu, wäre es
    // ohne Bedienung nicht wieder aufzubekommen.
    if (isStatic) return

    markers.forEach((marker) => marker.getPopup()?.remove())
}

async function initMap() {
    // maplibre-gl 6 exportiert nicht mehr über `default` wie noch v5 (das
    // aesthetify nutzt). Beide Formen abfangen, damit ein Versionssprung das
    // hier nicht wieder still bricht.
    const mod = await import('maplibre-gl')
    const maplibregl = mod.default ?? mod
    maplibre = maplibregl

    // Vor der ersten Karte, danach greift es nicht mehr: die Adresse wandert
    // beim Erzeugen des Workers einmal in den globalen Dispatcher.
    maplibregl.setWorkerUrl(maplibreWorkerUrl)

    const instance = new maplibregl.Map({
        style: 'https://tiles.openfreemap.org/styles/positron',
        zoom: zoomLevel.value,
        center: center.value,
        container: rootRef.value,
        // Statisch heisst: ein Kartenbild, kein Werkzeug. `interactive: false`
        // hängt alle Handler ab (Ziehen, Zoomen, Tastatur) — die Leinwand
        // bekommt dann auch keinen tabindex und liegt nicht im Tabweg.
        interactive: !isStatic,
        // Zoomen am Rad erst mit ctrl/⌘, Ziehen auf dem Touchscreen erst mit
        // zwei Fingern. Sonst bleibt die Seite beim Scrollen an der Karte hängen.
        // Ohne Bedienung braucht es den Hinweis nicht.
        cooperativeGestures: !isStatic,
        // Drehen und Kippen bringen auf einer Standortkarte nichts und kosten nur
        // die Nordung.
        dragRotate: false,
        pitchWithRotate: false,
        rollEnabled: false,
        // Aus, weil die eingebaute Attribution keine Ecke kennt — sie steht fest
        // unten rechts. Als eigene Steuerung darunter geht sie nach oben rechts.
        attributionControl: false,
        locale: {
            // Die Leinwand ist für Screenreader nichts — WebGL hat keinen Text.
            // maplibre hängt diesen Titel als Beschriftung an die Kartenregion.
            // Auf der statischen Karte OHNE Marker ist die gepflegte Adresse das
            // Einzige, was die Karte beschreibt — dann gehört sie hierher. Mit
            // Marker steht sie im Fähnchen, und hier wäre sie nur die zweite
            // Ansage derselben Zeile.
            'Map.Title': isStatic
                ? (address && !showMarker ? `Karte: ${address}` : 'Karte')
                : 'Karte der Standorte',
            'Marker.Title': 'Standort',
            'Popup.Close': 'Schliessen',
            'NavigationControl.ZoomIn': 'Hineinzoomen',
            'NavigationControl.ZoomOut': 'Herauszoomen',
            'AttributionControl.ToggleAttribution': 'Quellenangabe anzeigen',
            'CooperativeGesturesHandler.WindowsHelpText': 'Zum Zoomen ctrl + scrollen',
            'CooperativeGesturesHandler.MacHelpText': 'Zum Zoomen ⌘ + scrollen',
            'CooperativeGesturesHandler.MobileHelpText': 'Zum Bewegen zwei Finger nutzen',
        },
    })
    map.value = instance

    // Das ⓘ nach oben rechts: unten rechts stehen auf der bedienbaren Karte die
    // Zoom-Schalter, und der statischen fehlt dort die Ecke (die Kerbe aus
    // sections/_section-static-map.scss).
    instance.addControl(new maplibregl.AttributionControl({ compact: true }), 'top-right')

    if (!isStatic) {
        instance.touchZoomRotate.disableRotation()
        instance.addControl(
            new maplibregl.NavigationControl({ showCompass: false, showZoom: true }),
            'bottom-right',
        )
        releaseMapWheel = guardMapWheel(instance)
    }

    // Sobald von Hand bewegt wurde, nicht mehr auf die Standorte zurückspringen —
    // ein Resize (oder ein Filterklick) würde dem Nutzer sonst den Ausschnitt
    // unter der Hand wegziehen. `originalEvent` trennt Nutzergeste von
    // programmatischer Bewegung.
    instance.on('movestart', (event) => {
        if (event.originalEvent) userMoved = true
    })

    // maplibre meldet Quellen- und Tile-Fehler nur über dieses Event. Ohne
    // Listener schluckt es sie komplett — die Karte bleibt dann leer, ohne dass
    // in der Konsole etwas steht.
    instance.on('error', (e) => {
        console.error('[MapBox] maplibre:', e?.error?.message ?? e)
    })

    // Den Ausschnitt schon vor dem Style setzen — er braucht nur die Box.
    fitToLocations()

    instance.on('load', () => {
        instance.resize()

        applyBrandColors()

        // Die Quelle (und damit die Blasen) geht erst nach dem Style: Quellen und
        // Ebenen hängen am geladenen Style. Bleibt der aus, meldet das der
        // error-Handler oben.
        if (isStatic) {
            if (showMarker) {
                const pin = staticMarker().addTo(instance)
                markers = [pin]
                // Ohne Bedienung gibt es keinen Klick, der das Fähnchen öffnen
                // könnte — also gleich hier, mit der Karte.
                if (address || markerText) popup = addressPopup(pin.getElement()).addTo(instance)
            }
        } else {
            addLocationSource(instance)
            fitToLocations()
        }

        // MapLibre öffnet die kompakte Attribution initial und klappt sie erst
        // beim Draggen zu — was hier nie passiert. Also gleich zugeklappt.
        rootRef.value
            ?.querySelector('.maplibregl-ctrl-attrib')
            ?.classList.remove('maplibregl-compact-show')
    })

    // Die Kartenbox wächst mit dem Raster, ihre Höhe kommt aus aspect-ratio und
    // steht damit erst nach dem Layout fest. maplibre bekommt beides nicht von
    // allein mit: `resize()` zieht die Leinwand nach, `fitToLocations()` den
    // Ausschnitt.
    // Marken-Klasse am <html> (brand-geo / brand-gvo / brand-both) — wechselt sie
    // zur Laufzeit, färbt die Karte nach. Die Marker folgen über CSS von allein.
    if ('MutationObserver' in window) {
        mo = new MutationObserver(applyBrandColors)
        mo.observe(document.documentElement, { attributeFilter: ['class'] })
    }

    if ('ResizeObserver' in window) {
        let frame = null
        ro = new ResizeObserver(() => {
            if (frame) cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                instance.resize()
                fitToLocations()
            })
        })
        ro.observe(rootRef.value)
    }
}

// Alles, was initMap() aufgebaut hat, wieder abräumen. Nicht nur für das
// Unmount: ein Widerruf der Zustimmung muss die Karte wirklich beenden, sonst
// lädt sie beim Scrollen weiter Kacheln.
function destroyMap() {
    ro?.disconnect(); ro = null
    mo?.disconnect(); mo = null
    releaseMapWheel?.(); releaseMapWheel = null
    markers.forEach((marker) => marker.remove())
    markers = []
    popup?.remove(); popup = null
    map.value?.remove()
    map.value = null

    // Zustand der alten Karte, der einen Neustart nach erneuter Zustimmung sonst
    // verfälschte: `userMoved` hielte den Ausschnitt fest, `renderedIds` liesse
    // den Markerbau die Standorte für schon gesetzt halten.
    userMoved = false
    renderedIds = ''
    openPopupKey = null
}

onMounted(() => {
    if (!rootRef.value || !('IntersectionObserver' in window)) { inView.value = true; return }
    io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
            io.disconnect(); io = null
            inView.value = true
        }
    }, { rootMargin: '400px' })
    io.observe(rootRef.value)
})

// Sichtweite und Zustimmung treffen in beliebiger Reihenfolge ein — die
// Zustimmung womöglich erst Minuten später über das Banner. Deshalb ein Wächter
// auf beide statt eines Aufrufs am Ende des Observers. `starting` deckt die
// Lücke, die der dynamische Import von maplibre aufmacht.
watch([inView, consent], async ([visible, allowed]) => {
    if (!allowed) {
        if (map.value) destroyMap()
        return
    }

    if (!visible || map.value || starting) return

    starting = true
    try {
        await initMap()
    } finally {
        starting = false
    }
})

onUnmounted(() => {
    io?.disconnect(); io = null
    destroyMap()
})
</script>

<template>
    <div class="map-box">
        <div v-if="kinds.length" class="map-box__filters" role="group" aria-label="Karte filtern">
            <button
                type="button"
                class="map-box__filter"
                :class="{ 'is-active': activeKind === null }"
                :aria-pressed="activeKind === null"
                @click="showAll"
            >
                {{ allLabel }}
            </button>

            <button
                v-for="kind in kinds"
                :key="kind.handle"
                type="button"
                class="map-box__filter"
                :class="{ 'is-active': activeKind === kind.handle }"
                :aria-pressed="activeKind === kind.handle"
                @click="selectKind(kind.handle)"
            >
                {{ kind.title }}
            </button>
        </div>

        <div class="map-box__media text-media__surface" @keydown.esc="closePopups">
            <!-- Ersatzbild aus der Pflege (beide Kartensections geben eines
                 mit). Es steht im Fluss und gibt der Fläche ihre Höhe, die
                 Leinwand legt sich darüber. Bleibt maplibre aus — Ladefehler
                 oder fehlende Zustimmung —, bleibt das Bild stehen, ohne dass
                 hier etwas umschalten müsste. -->
            <slot />

            <div class="map-box__canvas" ref="rootRef"></div>

            <!-- Ohne Zustimmung startet maplibre nicht: die Kacheln kommen von
                 tiles.openfreemap.org und trügen die IP-Adresse dorthin. Der
                 Knopf erteilt die Zustimmung für „Präferenzen“ direkt und startet
                 die Karte damit auf der Stelle — er öffnet nicht das Banner.
                 Statistik und Marketing bleiben, wie sie stehen. -->
            <div v-if="!consent" class="map-box__consent">
                <div class="map-box__consent-box">
                    <p class="map-box__consent-text">{{ consentText }}</p>

                    <button type="button" class="cta-link" @click="grantConsent">
                        <span v-if="consentCtaParts.lead">{{ consentCtaParts.lead }}</span><span
                            class="cta-link__end"
                        >{{ consentCtaParts.last }}<ButtonArrow class="cta-link__icon" /></span>
                    </button>
                </div>
            </div>

            <!-- Der Filter ändert nur Punkte auf einer Karte: ohne Ansage bleibt
                 die Wirkung für Screenreader unsichtbar. Die statische Karte hat
                 keinen Filter — ihre Beschriftung steht als `Map.Title` an der
                 Kartenregion selbst. -->
            <p v-if="!isStatic" class="map-box__status visually-hidden" aria-live="polite">
                {{ visible.length }} von {{ parsed.length }} Standorten
            </p>
        </div>
    </div>
</template>
